/**
 * Generador de plantillas Excel/CSV para importación masiva
 */

import Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Genera plantilla CSV para productos
 */
export const generateProductsTemplate = () => {
  const template = [
    {
      nombre_producto: 'Ejemplo Alimento Premium',
      descripcion_producto: 'Alimento balanceado para perros adultos',
      tipo_categoria: 'Alimentos',
      categoria: 'Alimento para Perros'
    },
    {
      nombre_producto: 'Ejemplo Collar Ajustable',
      descripcion_producto: 'Collar de nylon resistente al agua',
      tipo_categoria: 'Accesorios',
      categoria: 'Collares'
    }
  ];

  return Papa.unparse(template);
};

/**
 * Genera plantilla CSV para items
 */
export const generateItemsTemplate = () => {
  const template = [
    {
      nombre_producto: 'Alimento Premium',
      descripcion_item: 'Alimento Premium - Sabor Pollo 15kg',
      sku: 'ALP-POL-15',
      color: 'N/A',
      talla: 'N/A',
      gramaje: '15kg',
      precio: '50000',
      stock: '100'
    },
    {
      nombre_producto: 'Collar Ajustable',
      descripcion_item: 'Collar Nylon - Rojo Mediano',
      sku: 'COL-ROJ-M',
      color: 'Rojo',
      talla: 'Mediano',
      gramaje: 'N/A',
      precio: '15000',
      stock: '50'
    }
  ];

  return Papa.unparse(template);
};

/**
 * Genera plantilla CSV combinada (productos + items)
 */
export const generateCombinedTemplate = () => {
  const template = [
    {
      tipo: 'PRODUCTO',
      nombre_producto: 'Alimento Premium',
      descripcion_producto: 'Alimento balanceado para perros adultos',
      tipo_categoria: 'Alimentos',
      categoria: 'Alimento para Perros',
      descripcion_item: '',
      sku: '',
      color: '',
      talla: '',
      gramaje: '',
      precio: '',
      stock: ''
    },
    {
      tipo: 'ITEM',
      nombre_producto: 'Alimento Premium',
      descripcion_producto: '',
      tipo_categoria: '',
      categoria: '',
      descripcion_item: 'Alimento Premium - Sabor Pollo 15kg',
      sku: 'ALP-POL-15',
      color: 'N/A',
      talla: 'N/A',
      gramaje: '15kg',
      precio: '50000',
      stock: '100'
    },
    {
      tipo: 'ITEM',
      nombre_producto: 'Alimento Premium',
      descripcion_producto: '',
      tipo_categoria: '',
      categoria: '',
      descripcion_item: 'Alimento Premium - Sabor Pollo 5kg',
      sku: 'ALP-POL-5',
      color: 'N/A',
      talla: 'N/A',
      gramaje: '5kg',
      precio: '20000',
      stock: '150'
    }
  ];

  return Papa.unparse(template);
};

/**
 * Descarga un archivo CSV
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Obtiene las columnas esperadas según el tipo de importación
 */
export const getExpectedColumns = (importType) => {
  const columns = {
    products: [
      'nombre_producto',
      'descripcion_producto',
      'tipo_categoria',
      'categoria'
    ],
    items: [
      'nombre_producto',
      'descripcion_item',
      'sku',
      'color',
      'talla',
      'gramaje',
      'precio',
      'stock'
    ],
    combined: [
      'tipo',
      'nombre_producto',
      'descripcion_producto',
      'tipo_categoria',
      'categoria',
      'descripcion_item',
      'sku',
      'color',
      'talla',
      'gramaje',
      'precio',
      'stock'
    ]
  };

  return columns[importType] || [];
};

/**
 * Genera plantilla Excel para importar SOLO ITEMS
 * Hoja 1: Productos (Referencia) - productos existentes
 * Hoja 2: Atributos (Referencia) - colores, tallas, gramajes
 * Hoja 3: Items - para crear
 */
export const generateExcelItemsTemplate = (systemData) => {
  const workbook = XLSX.utils.book_new();
  const { products = [], colors = [], sizes = [], weights = [] } = systemData;

  // Hoja 1: Productos Existentes (Referencia) - id_product para usar en la hoja Items
  const productsRefData = products.length > 0
    ? products.map(p => ({
        id_product: p.id_product ?? p.id,
        producto: p.name || p.product_name || '-',
        descripcion: p.description || '-',
        categoria: p.category?.category_name || '-'
      }))
    : [{ id_product: '(Sin productos)', producto: 'Crea productos primero', descripcion: '-', categoria: '-' }];

  const productsRefSheet = XLSX.utils.json_to_sheet(productsRefData);
  productsRefSheet['!cols'] = [
    { wch: 12 },
    { wch: 35 },
    { wch: 40 },
    { wch: 25 }
  ];
  XLSX.utils.book_append_sheet(workbook, productsRefSheet, 'Productos (Referencia)');

  // Hoja 2: Atributos (Referencia)
  const maxRows = Math.max(colors.length, sizes.length, weights.length, 1);
  const attributesData = [];
  for (let i = 0; i < maxRows; i++) {
    attributesData.push({
      colores: colors[i]?.color_name || '',
      tallas: sizes[i]?.size_name || '',
      gramajes: weights[i]?.weight_name || ''
    });
  }
  if (attributesData.length === 0 || (colors.length === 0 && sizes.length === 0 && weights.length === 0)) {
    attributesData.push({ colores: '(Puedes agregar nuevos)', tallas: '(Puedes agregar nuevos)', gramajes: '(Puedes agregar nuevos)' });
  }

  const attributesSheet = XLSX.utils.json_to_sheet(attributesData);
  attributesSheet['!cols'] = [
    { wch: 20 },
    { wch: 20 },
    { wch: 20 }
  ];
  XLSX.utils.book_append_sheet(workbook, attributesSheet, 'Atributos (Referencia)');

  // Hoja 3: Items (Para Crear) - usar id_product de la hoja Productos (Referencia), producto es solo referencia
  const itemsData = [
    {
      id_product: products[0]?.id_product ?? products[0]?.id ?? '',
      producto: products[0]?.name || '(Referencia - copia id_product de la hoja anterior)',
      descripcion_item: 'Descripción del item',
      sku: 'SKU-001',
      color: colors[0]?.color_name || 'N/A',
      talla: sizes[0]?.size_name || 'N/A',
      gramaje: weights[0]?.weight_name || 'N/A',
      precio: 10000,
      stock: 50
    }
  ];

  const itemsSheet = XLSX.utils.json_to_sheet(itemsData);
  itemsSheet['!cols'] = [
    { wch: 12 },
    { wch: 38 },
    { wch: 40 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
    { wch: 10 }
  ];
  XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');

  return workbook;
};

/**
 * Genera plantilla Excel para importar SOLO PRODUCTOS
 * Hoja 1: Categorías (Referencia) - categorías existentes
 * Hoja 2: Productos - para crear
 */
export const generateExcelProductsTemplate = (systemData) => {
  const workbook = XLSX.utils.book_new();
  const { categories = [] } = systemData;

  // Hoja 1: Categorías (Referencia)
  const categoriesRefData = categories.length > 0
    ? categories.map(c => ({
        tipo_categoria: c.categoryTypeName || c.type?.categoryTypeName || '-',
        categoria: c.category_name
      }))
    : [{ tipo_categoria: '(Sin categorías)', categoria: 'Crea categorías primero' }];

  const categoriesSheet = XLSX.utils.json_to_sheet(categoriesRefData);
  categoriesSheet['!cols'] = [
    { wch: 25 },
    { wch: 35 }
  ];
  XLSX.utils.book_append_sheet(workbook, categoriesSheet, 'Categorías (Referencia)');

  // Hoja 2: Productos (Para Crear)
  const productsData = [
    {
      nombre_producto: 'Nombre del Producto',
      descripcion_producto: 'Descripción del producto',
      tipo_categoria: categories[0]?.categoryTypeName || categories[0]?.type?.categoryTypeName || 'Tipo Categoría',
      categoria: categories[0]?.category_name || 'Categoría'
    }
  ];

  const productsSheet = XLSX.utils.json_to_sheet(productsData);
  productsSheet['!cols'] = [
    { wch: 30 },
    { wch: 50 },
    { wch: 20 },
    { wch: 25 }
  ];
  XLSX.utils.book_append_sheet(workbook, productsSheet, 'Productos');

  return workbook;
};

/**
 * Genera plantilla Excel para importar PRODUCTOS + ITEMS
 * Hoja 1: Categorías (Referencia)
 * Hoja 2: Atributos (Referencia)
 * Hoja 3: Productos - para crear
 * Hoja 4: Items - para crear
 */
export const generateExcelCombinedTemplate = (systemData) => {
  const workbook = XLSX.utils.book_new();
  const { categories = [], colors = [], sizes = [], weights = [] } = systemData;

  // Hoja 1: Categorías (Referencia)
  const categoriesRefData = categories.length > 0
    ? categories.map(c => ({
        tipo_categoria: c.categoryTypeName || c.type?.categoryTypeName || '-',
        categoria: c.category_name
      }))
    : [{ tipo_categoria: '(Sin categorías)', categoria: 'Crea categorías primero' }];

  const categoriesSheet = XLSX.utils.json_to_sheet(categoriesRefData);
  categoriesSheet['!cols'] = [
    { wch: 25 },
    { wch: 35 }
  ];
  XLSX.utils.book_append_sheet(workbook, categoriesSheet, 'Categorías (Referencia)');

  // Hoja 2: Atributos (Referencia)
  const maxRows = Math.max(colors.length, sizes.length, weights.length, 1);
  const attributesData = [];
  for (let i = 0; i < maxRows; i++) {
    attributesData.push({
      colores: colors[i]?.color_name || '',
      tallas: sizes[i]?.size_name || '',
      gramajes: weights[i]?.weight_name || ''
    });
  }
  if (attributesData.length === 0 || (colors.length === 0 && sizes.length === 0 && weights.length === 0)) {
    attributesData.push({ colores: '(Puedes agregar nuevos)', tallas: '(Puedes agregar nuevos)', gramajes: '(Puedes agregar nuevos)' });
  }

  const attributesSheet = XLSX.utils.json_to_sheet(attributesData);
  attributesSheet['!cols'] = [
    { wch: 20 },
    { wch: 20 },
    { wch: 20 }
  ];
  XLSX.utils.book_append_sheet(workbook, attributesSheet, 'Atributos (Referencia)');

  // Hoja 3: Productos (Para Crear)
  const productsData = [
    {
      nombre_producto: 'Nombre del Producto',
      descripcion_producto: 'Descripción del producto',
      tipo_categoria: categories[0]?.categoryTypeName || categories[0]?.type?.categoryTypeName || 'Tipo Categoría',
      categoria: categories[0]?.category_name || 'Categoría'
    }
  ];

  const productsSheet = XLSX.utils.json_to_sheet(productsData);
  productsSheet['!cols'] = [
    { wch: 30 },
    { wch: 50 },
    { wch: 20 },
    { wch: 25 }
  ];
  XLSX.utils.book_append_sheet(workbook, productsSheet, 'Productos');

  // Hoja 4: Items (Para Crear) - id_product y producto (referencia) de la hoja Productos
  const itemsData = [
    {
      id_product: '',
      producto: '(Copia id_product de la hoja Productos)',
      descripcion_item: 'Descripción del item',
      sku: 'SKU-001',
      color: colors[0]?.color_name || 'N/A',
      talla: sizes[0]?.size_name || 'N/A',
      gramaje: weights[0]?.weight_name || 'N/A',
      precio: 10000,
      stock: 50
    }
  ];

  const itemsSheet = XLSX.utils.json_to_sheet(itemsData);
  itemsSheet['!cols'] = [
    { wch: 12 },
    { wch: 38 },
    { wch: 40 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 12 },
    { wch: 10 }
  ];
  XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');

  return workbook;
};

/**
 * Genera plantilla Excel genérica (para compatibilidad)
 */
export const generateExcelTemplate = () => {
  return generateExcelCombinedTemplate({
    categories: [],
    colors: [],
    sizes: [],
    weights: [],
    products: []
  });
};

/**
 * Descarga un archivo Excel
 */
export const downloadExcel = (workbook, filename) => {
  XLSX.writeFile(workbook, filename);
};

/**
 * Lee un archivo Excel y retorna los datos de ambas hojas
 */
export const parseExcelFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Leer hoja de Productos
        let productsData = [];
        if (workbook.SheetNames.includes('Productos')) {
          const productsSheet = workbook.Sheets['Productos'];
          productsData = XLSX.utils.sheet_to_json(productsSheet);
        }
        
        // Leer hoja de Items
        let itemsData = [];
        if (workbook.SheetNames.includes('Items')) {
          const itemsSheet = workbook.Sheets['Items'];
          itemsData = XLSX.utils.sheet_to_json(itemsSheet);
        }
        
        resolve({
          products: productsData,
          items: itemsData,
          sheetNames: workbook.SheetNames
        });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};
